import React, { Component } from 'react';
import DashboardTemplate from "../../Common/DashboardTemplate";
import { AuthenticationService } from "../../../Services/AuthenticationService";
import axios from 'axios';
import {
    CircularProgress,
    Typography,
    Checkbox,
    Button,
    TextField,
} from "@material-ui/core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CustomerDiscountRulesPanel from "./CustomerDiscountRulesPanel";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const slugify = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

class HomepageCategories extends Component {
    state = {
        loading: true,
        saving: false,
        reordering: false,
        categories: [],
        drafts: {},
    };

    constructor(props) {
        super(props);
        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
        this.fileInputRef = React.createRef();
        this._uploadCategory = null;
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.isAdministrator) {
            AuthenticationService.getInstance().denyNonAdministratorAccess();
            return;
        }

        try {
            const response = await axios.get(`${apiBaseUrl}customer/catalog/categories/settings`);
            const categories = Array.isArray(response.data) ? response.data : [];
            const drafts = {};
            categories.forEach((c) => {
                drafts[c.sourceSlug] = {
                    description: c.description || '',
                    slug: c.slug || c.sourceSlug || '',
                };
            });
            this.setState({ categories, drafts, loading: false });
        } catch (error) {
            console.error('Error loading homepage categories:', error);
            window.$alertError('Failed to load homepage categories');
            this.setState({ loading: false });
        }
    };

    sourceKey = (category) => category.sourceSlug || category.slug;

    updateDraft = (sourceSlug, patch) => {
        this.setState((prev) => ({
            drafts: {
                ...prev.drafts,
                [sourceSlug]: {
                    ...(prev.drafts[sourceSlug] || {}),
                    ...patch,
                },
            },
        }));
    };

    saveCategory = async (category) => {
        const sourceSlug = this.sourceKey(category);
        const draft = this.state.drafts[sourceSlug] || {};
        const description = (draft.description || '').trim();
        const slug = slugify(draft.slug || sourceSlug);
        if (!description) {
            window.$alertError('Category name is required');
            return;
        }
        if (!slug) {
            window.$alertError('Slug must contain letters or numbers');
            return;
        }

        this.setState({ saving: true });
        try {
            const { data } = await axios.put(
                `${apiBaseUrl}customer/catalog/categories/settings/${encodeURIComponent(sourceSlug)}`,
                {
                    isVisible: category.isVisible,
                    description,
                    slug,
                }
            );
            this.setState((prev) => ({
                categories: prev.categories.map((c) =>
                    this.sourceKey(c) === sourceSlug
                        ? {
                            ...c,
                            ...data,
                            sourceSlug: data.sourceSlug || sourceSlug,
                        }
                        : c
                ),
                drafts: {
                    ...prev.drafts,
                    [sourceSlug]: {
                        description: data.description,
                        slug: data.slug,
                    },
                },
                saving: false,
            }));
        } catch (error) {
            console.error('Error saving category:', error);
            window.$alertError(error?.response?.data || 'Failed to save category');
            this.setState({ saving: false });
        }
    };

    handleToggle = async (category) => {
        const sourceSlug = this.sourceKey(category);
        const draft = this.state.drafts[sourceSlug] || {};
        this.setState({ saving: true });
        try {
            const { data } = await axios.put(
                `${apiBaseUrl}customer/catalog/categories/settings/${encodeURIComponent(sourceSlug)}`,
                {
                    isVisible: !category.isVisible,
                    description: draft.description || category.description,
                    slug: draft.slug || category.slug,
                }
            );
            this.setState((prev) => ({
                categories: prev.categories.map((c) =>
                    this.sourceKey(c) === sourceSlug
                        ? { ...c, ...data, sourceSlug: data.sourceSlug || sourceSlug }
                        : c
                ),
                saving: false,
            }));
        } catch (error) {
            console.error('Error updating category visibility:', error);
            window.$alertError('Failed to update category visibility');
            this.setState({ saving: false });
        }
    };

    openImagePicker = (category) => {
        this._uploadCategory = category;
        if (this.fileInputRef.current) this.fileInputRef.current.click();
    };

    handleImageFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        const category = this._uploadCategory;
        this._uploadCategory = null;
        if (!file || !category) return;

        const sourceSlug = this.sourceKey(category);
        this.setState({ saving: true });
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await axios.post(
                `${apiBaseUrl}customer/catalog/categories/settings/${encodeURIComponent(sourceSlug)}/image`,
                formData
            );
            const imageUrl = data && data.imageUrl != null ? data.imageUrl : undefined;
            this.setState((prev) => ({
                categories: prev.categories.map((c) =>
                    this.sourceKey(c) === sourceSlug
                        ? { ...c, imageUrl: imageUrl !== undefined ? imageUrl : c.imageUrl }
                        : c
                ),
                saving: false,
            }));
        } catch (error) {
            console.error('Error uploading category image:', error);
            window.$alertError('Failed to upload category image');
            this.setState({ saving: false });
        }
    };

    clearCategoryImage = async (category) => {
        if (!category.imageUrl) return;
        const sourceSlug = this.sourceKey(category);
        this.setState({ saving: true });
        try {
            await axios.delete(
                `${apiBaseUrl}customer/catalog/categories/settings/${encodeURIComponent(sourceSlug)}/image`
            );
            this.setState((prev) => ({
                categories: prev.categories.map((c) =>
                    this.sourceKey(c) === sourceSlug ? { ...c, imageUrl: null } : c
                ),
                saving: false,
            }));
        } catch (error) {
            console.error('Error removing category image:', error);
            window.$alertError('Failed to remove category image');
            this.setState({ saving: false });
        }
    };

    onDragEnd = (result) => {
        if (!result.destination) return;
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        if (sourceIndex === destinationIndex) return;

        const previous = this.state.categories;
        const categories = [...previous];
        const [moved] = categories.splice(sourceIndex, 1);
        categories.splice(destinationIndex, 0, moved);
        const ordered = categories.map((c, index) => ({ ...c, sortOrder: index }));
        this.setState({ categories: ordered, reordering: true });

        axios.put(`${apiBaseUrl}customer/catalog/categories/settings/reorder`, {
            orderedSourceSlugs: ordered.map((c) => this.sourceKey(c)),
        })
            .then(() => this.setState({ reordering: false }))
            .catch((error) => {
                console.error('Error reordering categories:', error);
                window.$alertError('Failed to save category order');
                this.setState({ categories: previous, reordering: false });
            });
    };

    renderCategoryRow = (category, index) => {
        const { saving, reordering, drafts } = this.state;
        const sourceSlug = this.sourceKey(category);
        const draft = drafts[sourceSlug] || { description: category.description, slug: category.slug };
        const busy = saving || reordering;

        return (
            <Draggable key={sourceSlug} draggableId={sourceSlug} index={index}>
                {(provided, snapshot) => (
                    <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                            ...provided.draggableProps.style,
                            backgroundColor: snapshot.isDragging ? '#e8f7f8' : undefined,
                        }}
                    >
                        <td style={{ textAlign: 'center', padding: '8px', width: 40, verticalAlign: 'middle' }}>
                            <span {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#666' }} title="Drag to reorder">
                                <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                        </td>
                        <td style={{ textAlign: 'left', padding: '8px', verticalAlign: 'middle' }}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Name"
                                value={draft.description}
                                disabled={busy}
                                onChange={(e) => this.updateDraft(sourceSlug, { description: e.target.value })}
                            />
                        </td>
                        <td style={{ textAlign: 'left', padding: '8px', verticalAlign: 'middle' }}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Slug"
                                value={draft.slug}
                                disabled={busy}
                                onChange={(e) => this.updateDraft(sourceSlug, { slug: e.target.value })}
                                onBlur={(e) => this.updateDraft(sourceSlug, { slug: slugify(e.target.value) })}
                            />
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {category.imageUrl ? (
                                    <img
                                        src={category.imageUrl}
                                        alt=""
                                        style={{ width: 48, height: 48, objectFit: 'cover', border: '1px solid #ccc', borderRadius: 2 }}
                                    />
                                ) : (
                                    <span style={{ fontSize: 12, color: '#999' }}>—</span>
                                )}
                                <Button variant="outlined" size="small" onClick={() => this.openImagePicker(category)} disabled={busy}>
                                    Upload
                                </Button>
                                {category.imageUrl ? (
                                    <Button variant="outlined" size="small" onClick={() => this.clearCategoryImage(category)} disabled={busy}>
                                        Clear
                                    </Button>
                                ) : null}
                            </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px', verticalAlign: 'middle' }}>
                            <Checkbox checked={!!category.isVisible} disabled />
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => this.saveCategory(category)}
                                    disabled={busy}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="contained"
                                    color={category.isVisible ? "secondary" : "primary"}
                                    size="small"
                                    onClick={() => this.handleToggle(category)}
                                    disabled={busy}
                                >
                                    {category.isVisible ? 'Hide' : 'Show'}
                                </Button>
                            </div>
                        </td>
                    </tr>
                )}
            </Draggable>
        );
    };

    render() {
        const { loading, categories, saving, reordering } = this.state;

        if (loading) {
            return (
                <DashboardTemplate title="Customer Site Controls" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                            <CircularProgress />
                        </div>
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="Customer Site Controls" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container" style={{ maxWidth: 1250 }}>
                        <h2 style={{ marginBottom: 8, marginTop: 0 }}>
                            Homepage Categories
                        </h2>
                        <Typography variant="body2" style={{ marginBottom: 10, color: '#666' }}>
                            Categories come from each product&apos;s primary type (Assembly/Subassembly Assembly Type, Part Part Type).
                            Drag to reorder homepage tiles. Edit name/slug then Save — product matching still uses the source type.
                        </Typography>
                        {(saving || reordering) && (
                            <Typography variant="body2" style={{ marginBottom: 8, color: '#1565c0' }}>
                                {reordering ? 'Saving order…' : 'Saving…'}
                            </Typography>
                        )}
                        <div className="parts-table-container">
                            <input
                                ref={this.fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                style={{ display: 'none' }}
                                onChange={this.handleImageFile}
                            />
                            <DragDropContext onDragEnd={this.onDragEnd}>
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }} />
                                            <th style={{ minWidth: 260 }}>Category name</th>
                                            <th style={{ minWidth: 180 }}>Public slug</th>
                                            <th style={{ minWidth: 160, textAlign: 'center' }}>Image</th>
                                            <th style={{ minWidth: 120, textAlign: 'center' }}>Show on Homepage</th>
                                            <th style={{ minWidth: 200, textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <Droppable droppableId="homepage-categories">
                                        {(provided) => (
                                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                {categories.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                                                            No categories found in current catalog.
                                                        </td>
                                                    </tr>
                                                ) : categories.map((category, index) => this.renderCategoryRow(category, index))}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                </table>
                            </DragDropContext>
                        </div>
                    </div>

                    <div className="container" style={{ maxWidth: 1250 }}>
                        <CustomerDiscountRulesPanel />
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

export default HomepageCategories;
